import type { RebalanceFormValues } from '@oneview/components/~advisor/Details/ModelDetails/RebalanceRecommendations/RebalanceForm';
import {
    AccountWithPositionInfo,
    Order,
    OrderTransaction,
    OrderWithExtraInfo,
    Quote,
    SecurityType,
} from '@orbis-systems/orbis-api-middleware';
import { createQueryFn, showAlertDialog, showToast } from '@orbis-systems/orbis-react-toolkit';
import { isAxiosError } from 'axios';
import { flatMap, get, groupBy, isObject, set } from 'lodash';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export type AllocationAccount = AccountWithPositionInfo & {
    allocationTransaction: OrderTransaction;
    allocationShares: number;
    allocationValue: number;
    newPositionValue: number;
    newPositionWeight: number;
};

export type AllocationResult = {
    accountNumbers: string[];
    allocationRef: string;
    avgPx: number;
    bond: boolean;
    option: boolean;
    orderRefs: string[];
    orders?: Order[];
    targets?: Order[];
    quantity: number;
    quote: Quote;
    symbol: string;
    totalAllocationQty: number;
    transaction: OrderTransaction.Buy | OrderTransaction.Sell;
};

export interface ScheduleAllocationParams {
    adjustmentId: number;
    symbol: string;
    allocations: {
        transaction: string;
        quantity: number;
        accountNumber: string;
    }[];
}

export interface Allocation {
    transaction: string;
    quote: {
        symbol: string;
    };
    targets: Target[];
}

export interface Target {
    quantity: number;
    account: {
        accountNumber: string;
    };
}

export interface PreallocationRequestParams {
    allocation: Allocation;
}

export type AllocationValidationResults = {
    [accountNumber: string]: {
        [symbol: string]: {
            [transaction: string]: string[];
        };
    };
};

const prepareMutualFundAllocation = (symbol: string, allocations: AllocationAccount[]) => {
    return allocations.map((allocation) => ({
        accountId: allocation.accountId,
        transaction: allocation.allocationTransaction,
        value: allocation.allocationValue,
        securityType: SecurityType.MutualFund,
        symbol: symbol,
    }));
};

export interface AllocationValidationError {
    id: string;
    lmid: string;
    errors: string[];
    timestamp: string;
}

export function isAllocationValidationError(value: unknown): value is AllocationValidationError {
    if (!isObject(value)) {
        return false;
    }

    const { id, lmid, errors, timestamp } = value as Record<string, unknown>;

    return (
        typeof id === 'string' &&
        typeof lmid === 'string' &&
        Array.isArray(errors) &&
        errors.every((error) => typeof error === 'string') &&
        typeof timestamp === 'string'
    );
}

export const allocationApi = api.injectEndpoints({
    endpoints: (build) => ({
        triggerAllocation: build.mutation({
            queryFn: createQueryFn(
                async (allocationRefs: string[], { extra: api, dispatch }) => {
                    const data: AllocationResult[] = await Promise.all(
                        allocationRefs.map(
                            async (allocationRef) =>
                                (await api.orbisSdk.post('v2/allocations/trigger', { allocationRef })).data,
                        ),
                    );
                    dispatch(showToast('Allocation has been triggered'));
                    return data;
                },
                { pendingMessage: 'Triggering allocation…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetModelDriftReport],
        }),
        scheduleAllocation: build.mutation({
            queryFn: createQueryFn(
                async ({ adjustmentId, symbol, allocations }: ScheduleAllocationParams, { extra: api }) => {
                    const transactions = groupBy(allocations, (allocation) => allocation.transaction);
                    const adjustment =
                        adjustmentId !== 0
                            ? {
                                  id: adjustmentId,
                              }
                            : undefined;
                    const data = Object.keys(transactions).map((transaction) => {
                        return {
                            adjustment,
                            allocation: {
                                transaction,
                                quote: {
                                    symbol,
                                },
                                targets: transactions[transaction]!.map((item) => ({
                                    quantity: item.quantity,
                                    account: {
                                        accountNumber: item.accountNumber,
                                    },
                                })),
                            },
                        };
                    });

                    const returnedData: AllocationResult[] = await Promise.all(
                        data.map(async (item) => {
                            const result = (await api.orbisSdk.post('v2/allocations/preallocate', item))
                                .data as AllocationResult;
                            return result;
                        }),
                    );

                    return returnedData;
                },
                { pendingMessage: 'Scheduling allocation…', alertIfError: false },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetModelDriftReport],
        }),
        validateAllocation: build.mutation({
            queryFn: createQueryFn(
                async (
                    params: {
                        allocationRef: string;
                        symbol: string;
                        transaction: OrderTransaction.Buy | OrderTransaction.Sell;
                    }[],
                    { extra: api },
                ) => {
                    const allocationValidationResult: AllocationValidationResults = {};
                    const responses: AllocationResult[] = [];
                    for (const item of params) {
                        const { allocationRef, symbol, transaction } = item;
                        try {
                            const response = await api.orbisSdk.post<AllocationResult>('v2/allocations/validate', {
                                allocationRef,
                            });

                            responses.push(response.data);
                        } catch (error) {
                            if (isAxiosError(error)) {
                                const errorData = error.response?.data;
                                if (isAllocationValidationError(errorData)) {
                                    const errors = (error.response?.data.errors || []) as string[];

                                    for (let i = 0; i < errors.length; i += 2) {
                                        const accountNumber = errors[i]!.split('=')[1];
                                        const errorMessage = errors[i + 1]!.split('=')[1];
                                        const path = `${accountNumber}.${symbol}.${transaction}`;
                                        set(allocationValidationResult, path, [
                                            ...(get(allocationValidationResult, path, []) as string[]),
                                            errorMessage,
                                        ]);
                                    }
                                } else {
                                    throw error;
                                }
                            } else {
                                throw error;
                            }
                        }
                    }

                    return { allocationValidationResult, responses };
                },
                { pendingMessage: 'Validating allocation…', alertIfError: false },
            ),
        }),
        validateAndScheduleAllocation: build.mutation({
            queryFn: createQueryFn(
                async (values: RebalanceFormValues, { extra: api, dispatch }) => {
                    const allocationValidationResult: AllocationValidationResults = {};
                    const allocationGroupedBySymbol = groupBy(
                        values.selectedAccountNumbers
                            .map((item) => {
                                const [accountNumber, symbol] = item.split(':');
                                return {
                                    accountNumber: accountNumber!,
                                    symbol: symbol!,
                                    transaction: values.transaction[item]!,
                                    quantity: values.quantity[item]!,
                                };
                            })
                            .filter((item) => item.quantity > 0),
                        (item) => item.symbol,
                    );

                    const preallocationRes: (AllocationResult | undefined)[] = flatMap(
                        await Promise.all(
                            Object.values(allocationGroupedBySymbol).map((allocations) => {
                                return dispatch(
                                    allocationApi.endpoints.scheduleAllocation.initiate({
                                        adjustmentId: 0,
                                        symbol: allocations[0]!.symbol,
                                        allocations,
                                    }),
                                )
                                    .unwrap()
                                    .catch((error) => {
                                        if (isAxiosError(error)) {
                                            const errorMessage = error.response?.data.error || 'Something went wrong';

                                            const requestData: PreallocationRequestParams = JSON.parse(
                                                error.config?.data,
                                            );

                                            const {
                                                allocation: {
                                                    targets,
                                                    transaction,
                                                    quote: { symbol },
                                                },
                                            } = requestData;

                                            targets.forEach((target) => {
                                                const {
                                                    account: { accountNumber },
                                                } = target;

                                                allocationValidationResult[accountNumber] = {
                                                    ...allocationValidationResult[accountNumber],
                                                    [symbol]: {
                                                        ...allocationValidationResult[accountNumber]?.[symbol],
                                                        [transaction]: [errorMessage],
                                                    },
                                                };
                                            });
                                        }

                                        return Promise.resolve();
                                    });
                            }),
                        ),
                    );

                    const preAllocationData = preallocationRes.filter((item) =>
                        Boolean(item?.allocationRef),
                    ) as AllocationResult[];

                    for (const item of preAllocationData) {
                        const { allocationRef, symbol, transaction } = item!;
                        try {
                            await api.orbisSdk.post('v2/allocations/validate', { allocationRef });
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } catch (error: any) {
                            if (isAxiosError(error)) {
                                const errors = (error.response?.data.errors || []) as string[];

                                for (let i = 0; i < errors.length; i += 2) {
                                    const accountNumber = errors[i]!.split('=')[1];
                                    const errorMessage = errors[i + 1]!.split('=')[1];
                                    const path = `${accountNumber}.${symbol}.${transaction}`;
                                    set(allocationValidationResult, path, [
                                        ...(get(allocationValidationResult, path, []) as string[]),
                                        errorMessage,
                                    ]);
                                }
                            } else {
                                throw error;
                            }
                        }
                    }

                    return {
                        allocationValidationResult,
                        allocationRefs: preAllocationData.map((allocation) => allocation.allocationRef),
                        preAllocationData,
                    };
                },
                { pendingMessage: 'Validating allocation…', alertIfError: false },
            ),
        }),
        submitAllocation: build.mutation({
            queryFn: createQueryFn(
                async (
                    { orders, allocations }: { orders: OrderWithExtraInfo[]; allocations: AllocationAccount[] },
                    { extra: api, dispatch },
                ) => {
                    const body = {
                        orders: orders.map((order) => ({
                            ourRef: order.ourRef,
                            quantity: order.quantity,
                        })),
                        targets: allocations.map((allocation) => ({
                            quantity: allocation.allocationShares,
                            account: {
                                accountNumber: allocation.accountNumber,
                            },
                        })),
                    };

                    const response = await api.orbisSdk.post('v2/advisory/model/orders/allocate', body);
                    dispatch(showToast('Allocation submitted'));
                    return response.data.AllocationRef as string;
                },
                { pendingMessage: 'Submitting allocation…', alertIfError: false },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetModelDriftReport],
        }),
        getAllocationAccounts: build.query({
            queryFn: createQueryFn(
                async ({ symbol, accountIds }: { symbol: string; accountIds: number[] }, { extra: api, dispatch }) => {
                    try {
                        return await api.orbisMiddleware.account.getAccountWithPositionInfo(symbol, ...accountIds);
                    } catch {
                        dispatch(showAlertDialog('Failed to load accounts information'));
                        return [];
                    }
                },
                { pendingMessage: 'Getting accounts information…' },
            ),
        }),
        previewMutualFundAllocation: build.mutation({
            queryFn: createQueryFn(
                async (
                    { symbol, allocations }: { symbol: string; allocations: AllocationAccount[] },
                    { extra: api },
                ) => {
                    const preparedOrders = prepareMutualFundAllocation(symbol, allocations);
                    return Promise.all(
                        preparedOrders.map(
                            async (item) => (await api.orbisSdk.post('orders/v2/mf/preview', item)).data,
                        ),
                    );
                },
                { pendingMessage: 'Preview allocation…' },
            ),
        }),
        submitMutualFundAllocation: build.mutation({
            queryFn: createQueryFn(
                async (
                    { symbol, allocations }: { symbol: string; allocations: AllocationAccount[] },
                    { extra: api, dispatch },
                ) => {
                    const preparedOrders = prepareMutualFundAllocation(symbol, allocations);
                    const data = (
                        await Promise.all(preparedOrders.map((item) => api.orbisSdk.post('orders/v2/mf/place', item)))
                    ).map((item) => item.data.orderRef);
                    dispatch(showToast('Allocation submitted'));
                    return data as string[];
                },
                { pendingMessage: 'Submitting allocation…' },
            ),
            invalidatesTags: [DataTags.GetModels, DataTags.GetModel, DataTags.GetModelDriftReport],
        }),
    }),
});

export const {
    useTriggerAllocationMutation,
    useValidateAndScheduleAllocationMutation,
    useScheduleAllocationMutation,
    useSubmitAllocationMutation,
    useLazyGetAllocationAccountsQuery,
    usePreviewMutualFundAllocationMutation,
    useSubmitMutualFundAllocationMutation,
} = allocationApi;
